import { Button, EmptyState, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { acceptInvitation, fetchInvitation, type OpenInvitation } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'

/**
 * Where an invitation e-mail lands (M3-02). The invited person has no account yet, so this page is
 * outside the signed-in shell: it says who invited them and with which role, and accepting sends a
 * magic link to the invited address — never to whichever address the visitor types. The mailbox is
 * what proves the person is the one who was invited; the membership itself is granted by the API
 * the moment that link is opened.
 */
export const Route = createFileRoute('/invite/$invitationId')({ component: Invite })

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'open'; readonly invitation: OpenInvitation }
  | { readonly status: 'sent'; readonly email: string }
  | { readonly status: 'invalid' }

function Invite() {
  const { invitationId } = Route.useParams()
  const { t } = useConsoleI18n()
  const [state, setState] = useState<State>({ status: 'loading' })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    fetchInvitation(invitationId, controller.signal)
      .then((invitation) => setState({ status: 'open', invitation }))
      .catch(() => {
        if (controller.signal.aborted) return
        // Revoked, expired, already used or simply wrong: one answer, and no way to tell which.
        setState({ status: 'invalid' })
      })
    return () => controller.abort()
  }, [invitationId])

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }

  if (state.status === 'invalid') {
    return <EmptyState title={t('accept.invalid.title')} description={t('accept.invalid.body')} />
  }

  if (state.status === 'sent') {
    return (
      <section className="mx-auto flex max-w-md flex-col gap-3">
        <h1 className="font-semibold text-display text-ink">{t('accept.sent.title')}</h1>
        <p className="text-body text-muted">{t('accept.sent.body', { email: state.email })}</p>
      </section>
    )
  }

  const { invitation } = state

  return (
    <section className="mx-auto flex max-w-md flex-col gap-5">
      <h1 className="font-semibold text-display text-ink">{t('accept.title')}</h1>
      <p className="text-body text-muted">
        {t('accept.intro', {
          organization: invitation.organization_name,
          role: t(`members.role.${invitation.role}`),
          email: invitation.email,
        })}
      </p>
      <Button
        block
        busy={busy}
        busyLabel={t('loading')}
        onClick={() => {
          setBusy(true)
          acceptInvitation(invitation.id)
            .then(() => setState({ status: 'sent', email: invitation.email }))
            .catch(() => setState({ status: 'invalid' }))
            .finally(() => setBusy(false))
        }}
      >
        {t('accept.submit')}
      </Button>
    </section>
  )
}
